import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import Sidebar from "./UserSidebar";
import Header from "./UserHeader";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import { apiFetch } from "../api_call";

export default function Compliant_() {

  const [menuOpen, setMenuOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "asc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const rowsPerPage = 8;

  const navigate = useNavigate();

  // ================= FETCH DATA =================
  useEffect(() => {

    const fetchRiskData = async () => {

      try {

        setLoading(true);
        setError(null);

        const res =
          await apiFetch("/report/user/risk-report");

        const formatted =
          (res.compliant || []).map(item => ({

            id: item.id,
            act: item.act || item.type,
            particular: item.particular || "",

            startDate: item.startDate,
            actionDate: item.actionDate,
            endDate: item.endDate,

            status: item.status,

            requestDate: item.requestDate || "",
            responseDate: item.responseDate || ""

          }));

        setData(formatted);

      } catch (err) {

        console.error("Error fetching risk report:", err);
        setError("Failed to fetch compliant records.");

      } finally {

        setLoading(false);

      }

    };

    fetchRiskData();

  }, []);

  // ================= SEARCH =================
  const filteredData = useMemo(() => {

    if (!searchTerm) return data;

    return data.filter(item =>
      Object.values(item)
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );

  }, [data, searchTerm]);

  // ================= SORT =================
  const sortedData = useMemo(() => {

    let sortable = [...filteredData];

    if (sortConfig.key) {

      sortable.sort((a, b) => {

        const valA =
          (a[sortConfig.key] || "")
            .toString()
            .toLowerCase();

        const valB =
          (b[sortConfig.key] || "")
            .toString()
            .toLowerCase();

        if (valA < valB)
          return sortConfig.direction === "asc" ? -1 : 1;

        if (valA > valB)
          return sortConfig.direction === "asc" ? 1 : -1;

        return 0;

      });

    }

    return sortable;

  }, [filteredData, sortConfig]);

  const requestSort = (key) => {

    let direction = "asc";

    if (
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    )
      direction = "desc";

    setSortConfig({ key, direction });

  };

  const getSortIcon = (key) => {

    if (sortConfig.key !== key)
      return <FaSort className="sort-icon" />;

    return sortConfig.direction === "asc"
      ? <FaSortUp className="sort-icon" />
      : <FaSortDown className="sort-icon" />;

  };

  // ================= PAGINATION =================
  const indexOfLastRow =
    currentPage * rowsPerPage;

  const indexOfFirstRow =
    indexOfLastRow - rowsPerPage;

  const currentRows =
    sortedData.slice(indexOfFirstRow, indexOfLastRow);

  const totalPages =
    Math.ceil(sortedData.length / rowsPerPage);

  // ================= EXPORT =================
  const exportToCSV = () => {

    if (!sortedData.length) return;

    const headers = [
      "Id",
      "Act",
      "Instance",
      "Start Date",
      "Action Date",
      "End Date",
      "Status",
      "Request Date",
      "Response Date"
    ];

    const rows = sortedData.map(r =>
      [
        r.id,
        r.act,
        r.particular,
        r.startDate,
        r.actionDate,
        r.endDate,
        r.status,
        r.requestDate,
        r.responseDate
      ].map(v => `"${v || ""}"`)
    );

    const csv =
      [headers.join(","), ...rows.map(r => r.join(","))]
        .join("\n");

    const blob =
      new Blob([csv],
        { type: "text/csv;charset=utf-8;" });

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;

    link.setAttribute(
      "download",
      "compliant_compliance.csv"
    );

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

  };

  // ================= UI =================
  return (

    <div className="Approved">

      <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">
          Compliant Report
        </div>
      </div>

      <div className="table-header" style={{ display: "flex", justifyContent: "space-between" }}>

        <div style={{ display: "flex", gap: "10px", marginLeft: "45px" }}>

          <button
            className="btn blue-btn"
            onClick={() => navigate("/at-risk_")}
          >
            At Risk
          </button>

          <button
            className="btn blue-btn"
            onClick={() => navigate("/low-risk_")}
          >
            Low Risk
          </button>

          <button className="btn gray-btn">
            Compliant
          </button>

        </div>

        <div className="table-actions"
          style={{ display: "flex", gap: "10px" }}>

          <div className="search-box">

            <input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
            />

            <FaSearch className="search-icon" />

          </div>

          <button
            className="action-btn primary"
            onClick={exportToCSV}
          >
            Export
          </button>

        </div>

      </div>

      <table className="data-table">

        <thead>

          <tr>

            <th onClick={() => requestSort("id")}>
              Id {getSortIcon("id")}
            </th>

            <th onClick={() => requestSort("act")}>
              Compliance {getSortIcon("act")}
            </th>

            <th>
              Instance
            </th>

            <th onClick={() => requestSort("startDate")}>
              Start Date
            </th>

            <th onClick={() => requestSort("actionDate")}>
              Action Date
            </th>

            <th onClick={() => requestSort("endDate")}>
              End Date
            </th>

            <th onClick={() => requestSort("status")}>
              Status
            </th>

            <th onClick={() => requestSort("requestDate")}>
              Request Date
            </th>

            <th onClick={() => requestSort("responseDate")}>
              Response Date
            </th>

          </tr>

        </thead>

        <tbody>

          {loading ? (

            <tr>
              <td colSpan="11" style={{ textAlign: "center", padding: "20px" }}>
                Loading records...
              </td>
            </tr>

          ) : error ? (

            <tr>
              <td colSpan="11" style={{ textAlign: "center", color: "red" }}>
                {error}
              </td>
            </tr>

          ) : currentRows.length > 0 ? (

            currentRows.map((row, index) => (

              <tr key={index}>

                <td>{row.id}</td>
                <td>{row.act}</td>

                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>{row.particular}</span>
                    <IoIosInformationCircle
                      style={{
                        cursor: "pointer",
                        fontSize: "16px",
                        color: "#1A1744"
                      }}
                    />
                  </div>
                </td>

                <td>{row.startDate}</td>
                <td>{row.actionDate}</td>
                <td>{row.endDate}</td>

                <td
                  style={{
                    color:
                      row.status?.toLowerCase() === "approved"
                        ? "green"
                        : row.status?.toLowerCase() === "requested"
                        ? "orange"
                        : "red",
                    fontWeight: 600
                  }}
                >
                  {row.status}
                </td>

                <td>{row.requestDate}</td>
                <td>{row.responseDate}</td>

              </tr>

            ))

          ) : (

            <tr>
              <td colSpan="11" style={{ textAlign: "center" }}>
                No compliant records found
              </td>
            </tr>

          )}

        </tbody>

      </table>

      <div className="pagination">

        <button
          disabled={currentPage === 1}
          onClick={() =>
            setCurrentPage(p => p - 1)
          }
        >
          Prev
        </button>

        <span>
          Page {currentPage} of {totalPages}
        </span>

        <button
          disabled={currentPage === totalPages}
          onClick={() =>
            setCurrentPage(p => p + 1)
          }
        >
          Next
        </button>

      </div>

    </div>
  );
}